import {
  allowanceErrorContent,
  type Step,
  type TokenUsage,
} from '@toggle/toggle';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { config } from '~/config';
import { CloseReason } from '~/services/base-ws/BaseWs';
import { Tracking } from '~/services/tracking';
import { useAuthStore } from '~/stores/use-auth/store/authStore';
import {
  TOKEN_ALLOWANCE_FEATURE_FLAG,
  useFlags,
} from '~/stores/use-flags/useFlags';
import { useTokenUsage } from '~/stores/use-token-usage/useTokenUsage';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import { getInProgressConversations } from './useNotificationsWs.utils';

const TOKEN_USAGE_EVENT_TYPE = 'token.usage';

interface NotificationPayload {
  metadata?: unknown;
  event: {
    type: string;
    action: string;
  };
}

export interface AlfredResponsePayload extends NotificationPayload {
  title: string;
  body: string;
  metadata: {
    conversation_id: string;
    request_id: string;
    record_id?: string;
  };
  event: {
    type: 'conversation';
    action:
      | 'request.update'
      | 'request.success'
      | 'request.fail'
      | 'request.cancel';
  };
}

export interface TokenUsageResponsePayload extends NotificationPayload {
  metadata: TokenUsage;
  event: {
    type: typeof TOKEN_USAGE_EVENT_TYPE;
    action: 'usage.updated';
  };
}

const isTokenUsageResponse = (
  message: NotificationPayload
): message is TokenUsageResponsePayload =>
  message.event.type === TOKEN_USAGE_EVENT_TYPE &&
  message.event.action === 'usage.updated';

const isConversationResponse = (
  message: NotificationPayload
): message is AlfredResponsePayload => message.event.type === 'conversation';

export type ConnectionStatus =
  | 'connecting'
  | 'open'
  | 'closed'
  | 'error'
  | 'httpPolling';

type PollingReason = 'reconnection_limit' | 'connect_timeout';

const NORMAL_CLOSURE = 1000;

let wsStatus: ConnectionStatus = 'closed';
const reactSubscribers = new Set<() => void>();

export const useNotificationsWs = () => {
  const token = useAuthStore(s => s.token);
  const isTokenUsageEnabled = useFlags(state =>
    state.getFeatureFlagState(TOKEN_ALLOWANCE_FEATURE_FLAG)
  );
  const { applyUsage, fetchUsage, setExhausted, setUnavailable } =
    useTokenUsage(state => ({
      applyUsage: state.applyUsage,
      fetchUsage: state.fetchUsage,
      setExhausted: state.setExhausted,
      setUnavailable: state.setUnavailable,
    }));
  const {
    getConversationStates,
    markConversationReadStatus,
    markTaskReceivedWsUpdate,
  } = useChat(state => ({
    getConversationStates: state.getConversationStates,
    markConversationReadStatus: state.markConversationReadStatus,
    markTaskReceivedWsUpdate: state.markTaskReceivedWsUpdate,
  }));

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectionIdRef = useRef(0);
  const connectTimeoutIdRef = useRef<number | null>(null);
  const reconnectionRef = useRef(0);
  const pollingIntervalIdRef = useRef<number | null>(null);
  const pollingInFlightRef = useRef(false);
  const isTokenUsageEnabledRef = useRef(isTokenUsageEnabled);
  isTokenUsageEnabledRef.current = isTokenUsageEnabled;

  const subscribeToConnectionStatus = useCallback(
    (onChange: () => void): (() => void) => {
      reactSubscribers.add(onChange);

      return () => {
        reactSubscribers.delete(onChange);
      };
    },
    []
  );

  const getConnectionStatusSnapshot = useCallback(
    (): ConnectionStatus => wsStatus,
    []
  );

  const connectionStatus = useSyncExternalStore(
    subscribeToConnectionStatus,
    getConnectionStatusSnapshot
  );

  const notifyReactSubscribers = useCallback(
    () => reactSubscribers.forEach(fn => fn()),
    []
  );

  const applyAllowanceFailure = useCallback(
    (states: Step[]) => {
      const lastState = states.at(-1);

      if (lastState?.status !== 'Error') {
        return;
      }

      if (lastState.content === allowanceErrorContent.unavailable) {
        setUnavailable('service-unavailable');
      } else if (lastState.content === allowanceErrorContent.exceeded) {
        setExhausted({ source: 'conversation' });
        fetchUsage('conversation');
      }
    },
    [fetchUsage, setExhausted, setUnavailable]
  );

  const stopPolling = useCallback(() => {
    if (pollingIntervalIdRef.current !== null) {
      window.clearInterval(pollingIntervalIdRef.current);
      pollingIntervalIdRef.current = null;
    }

    pollingInFlightRef.current = false;
  }, []);

  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutIdRef.current !== null) {
      window.clearTimeout(connectTimeoutIdRef.current);
      connectTimeoutIdRef.current = null;
    }
  }, []);

  const syncInProgressConversationStates = useCallback(async () => {
    const chatState = useChat.getState();
    const targets = getInProgressConversations(chatState.conversations);

    for (const target of targets) {
      try {
        const states = await chatState.getConversationStates(target);
        applyAllowanceFailure(states);

        const updatedConv = useChat
          .getState()
          .conversations.find(c => c.id === target.conversationId);

        if (updatedConv && updatedConv.status !== 'Processing') {
          chatState.markConversationReadStatus(target.conversationId, false);
        }
      } catch (error) {
        Tracking.captureException(error, {
          context: 'useNotificationsWs/syncInProgressConversationStates',
          conversationId: target.conversationId,
          taskId: target.taskId,
        });
      }
    }
  }, [applyAllowanceFailure]);

  const startPolling = useCallback(
    ({
      reconnectionAttempts,
      lastCloseCode,
      reason,
    }: {
      reconnectionAttempts: number;
      lastCloseCode: number;
      reason: PollingReason;
    }) => {
      stopPolling();

      wsStatus = 'httpPolling';
      notifyReactSubscribers();

      if (reason === 'reconnection_limit') {
        Tracking.captureException(
          new Error(
            'WebSocket max reconnections exceeded, falling back to HTTP polling'
          ),
          {
            context: 'useNotificationsWs/httpPolling',
            reconnectionAttempts: String(reconnectionAttempts),
            reconnectionLimit: String(config.ws.reconnectionLimit),
            lastCloseCode: String(lastCloseCode),
          }
        );
      }

      const poll = async () => {
        if (pollingInFlightRef.current) {
          return;
        }

        pollingInFlightRef.current = true;

        try {
          await syncInProgressConversationStates();
        } finally {
          pollingInFlightRef.current = false;
        }
      };

      pollingIntervalIdRef.current = window.setInterval(
        poll,
        config.ws.pollingInterval
      );
    },
    [stopPolling, notifyReactSubscribers, syncInProgressConversationStates]
  );

  const closeWs = useCallback(() => {
    clearTimeout(reconnectionIdRef.current);
    clearConnectTimeout();
    stopPolling();
    wsRef.current?.close(NORMAL_CLOSURE, CloseReason.NoConnectionRequired);
    wsRef.current = null;
    reconnectionRef.current = 0;
    wsStatus = 'closed';
    notifyReactSubscribers();
  }, [clearConnectTimeout, notifyReactSubscribers, stopPolling]);

  const handleConversationMessage = useCallback(
    async (response: AlfredResponsePayload) => {
      const { conversation_id: conversationId, request_id: requestId } =
        response.metadata;

      markTaskReceivedWsUpdate(conversationId, requestId);

      const states = await getConversationStates({
        conversationId,
        taskId: requestId,
      });

      applyAllowanceFailure(states);

      if (response.event.action !== 'request.fail') {
        markConversationReadStatus(
          conversationId,
          response.event.action === 'request.cancel'
        );
      }
    },
    [
      applyAllowanceFailure,
      getConversationStates,
      markConversationReadStatus,
      markTaskReceivedWsUpdate,
    ]
  );

  // Ref keeps onmessage pointing at the latest handler without the WS effect
  // needing to depend on it (which would cause an unnecessary reconnect).
  const handleConversationMessageRef = useRef(handleConversationMessage);
  handleConversationMessageRef.current = handleConversationMessage;

  useEffect(() => {
    if (!token) {
      closeWs();
      return undefined;
    }

    const handleTokenUsageMessage = (message: TokenUsageResponsePayload) => {
      if (!isTokenUsageEnabledRef.current) {
        return;
      }

      applyUsage(message.metadata, 'websocket');
    };

    const openWs = (url: string) => {
      wsRef.current = new WebSocket(url, ['bearer', token]);
      wsStatus = 'connecting';
      notifyReactSubscribers();

      clearConnectTimeout();
      connectTimeoutIdRef.current = window.setTimeout(() => {
        if (wsStatus !== 'connecting') {
          return;
        }

        Tracking.captureException(
          new Error('WebSocket connect timeout, switching to HTTP polling'),
          {
            context: 'useNotificationsWs/connectTimeout',
            timeoutMs: String(config.ws.connectTimeout),
          }
        );

        clearTimeout(reconnectionIdRef.current);
        wsRef.current?.close(NORMAL_CLOSURE, CloseReason.NoConnectionRequired);
        wsRef.current = null;

        startPolling({
          reconnectionAttempts: reconnectionRef.current,
          lastCloseCode: 1006,
          reason: 'connect_timeout',
        });
      }, config.ws.connectTimeout);

      wsRef.current.onopen = () => {
        clearConnectTimeout();
        wsStatus = 'open';
        notifyReactSubscribers();

        if (isTokenUsageEnabledRef.current && reconnectionRef.current > 0) {
          fetchUsage('reconnect');
        }
      };

      wsRef.current.onerror = () => {
        clearConnectTimeout();
        wsStatus = 'error';
        notifyReactSubscribers();
      };

      wsRef.current.onmessage = (event: MessageEvent) => {
        const rawData: string = event.data;

        try {
          const message: NotificationPayload = JSON.parse(rawData);

          if (isTokenUsageResponse(message)) {
            handleTokenUsageMessage(message);
            return;
          }

          if (!isConversationResponse(message)) {
            return;
          }

          handleConversationMessageRef.current(message).catch(err =>
            Tracking.captureException(err, {
              context: 'useNotificationsWs/handleConversationMessage',
              conversationId: message.metadata.conversation_id,
              requestId: message.metadata.request_id,
              action: message.event.action,
            })
          );
        } catch (error) {
          Tracking.captureException(error, {
            context: 'useNotificationsWs/onmessage',
            eventData: rawData,
          });
        }
      };

      wsRef.current.onclose = (closeEvent: CloseEvent) => {
        clearConnectTimeout();

        if (closeEvent.reason === CloseReason.NoConnectionRequired) {
          return;
        }

        const hadError = wsStatus === 'error';
        wsStatus = 'closed';
        notifyReactSubscribers();

        if (hadError || !closeEvent.wasClean) {
          syncInProgressConversationStates();
        }

        if (reconnectionRef.current >= config.ws.reconnectionLimit) {
          startPolling({
            reconnectionAttempts: reconnectionRef.current,
            lastCloseCode: closeEvent.code,
            reason: 'reconnection_limit',
          });
          return;
        }

        reconnectionIdRef.current = window.setTimeout(
          () => openWs(url),
          config.ws.reconnectionTimeout
        );
        reconnectionRef.current++;
      };
    };

    openWs(`${config.api.wsRoot}v2/notifier`);

    return () => {
      closeWs();
    };
  }, [
    token,
    closeWs,
    clearConnectTimeout,
    applyUsage,
    fetchUsage,
    notifyReactSubscribers,
    startPolling,
    syncInProgressConversationStates,
  ]);

  return { connectionStatus };
};
